import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";

const STATUSES = ["Present", "Absent", "Leave", "Half Day", "Remote"];

function todayInput() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function toTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toTimeString().slice(0, 5);
}

function toDateTime(date, time) {
  return time ? `${date}T${time}:00` : null;
}

export default function AttendancePage() {
  const [date, setDate] = useState(todayInput());
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [employeesRes, attendanceRes] = await Promise.all([
        apiFetch("/api/hrms/employees?status=Active"),
        apiFetch(`/api/hrms/attendance?date=${encodeURIComponent(date)}`),
      ]);
      if (!employeesRes.ok) throw new Error(await employeesRes.text());
      if (!attendanceRes.ok) throw new Error(await attendanceRes.text());
      const nextEmployees = await employeesRes.json();
      const nextRecords = await attendanceRes.json();
      setEmployees(Array.isArray(nextEmployees) ? nextEmployees : []);
      setRecords(Array.isArray(nextRecords) ? nextRecords : []);
    } catch (err) {
      setError(err?.message || "Could not load attendance.");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const recordMap = useMemo(() => {
    return new Map(records.map((record) => [record.employeeId, record]));
  }, [records]);

  useEffect(() => {
    const next = {};
    for (const employee of employees) {
      const record = recordMap.get(employee.id);
      next[employee.id] = {
        status: record?.status || "Present",
        checkInAt: toTimeInput(record?.checkInAt),
        checkOutAt: toTimeInput(record?.checkOutAt),
        note: record?.note || "",
      };
    }
    setDrafts(next);
  }, [employees, recordMap]);

  function updateDraft(employeeId, field, value) {
    setDrafts((current) => ({
      ...current,
      [employeeId]: {
        ...(current[employeeId] || {}),
        [field]: value,
      },
    }));
  }

  async function saveRecord(employeeId) {
    const draft = drafts[employeeId] || {};
    setSavingId(employeeId);
    setError("");
    try {
      const res = await apiFetch("/api/hrms/attendance", {
        method: "POST",
        body: JSON.stringify({
          employeeId,
          date: `${date}T00:00:00`,
          status: draft.status || "Present",
          checkInAt: toDateTime(date, draft.checkInAt),
          checkOutAt: toDateTime(date, draft.checkOutAt),
          note: draft.note || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (err) {
      setError(err?.message || "Could not save attendance.");
    } finally {
      setSavingId("");
    }
  }

  return (
    <div className="hrms-page">
      <PageHeader
        title="Attendance"
        subtitle="Mark daily attendance for active employees."
        actions={
          <button className="btn-ghost" onClick={load} disabled={loading}>
            Refresh
          </button>
        }
      />

      <div className="glass-card hrms-toolbar">
        <label>
          Attendance Date
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </label>
        <div className="hrms-toolbar-meta">
          {records.length} marked of {employees.length} active employees
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card hrms-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4, 5].map((item) => (
              <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />
            ))}
          </div>
        ) : employees.length === 0 ? (
          <EmptyState
            title="No active employees"
            subtitle="Add active employees before marking attendance."
          />
        ) : (
          <div className="hrms-table-wrap">
            <table className="hrms-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Status</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => {
                  const draft = drafts[employee.id] || {};
                  return (
                    <tr key={employee.id}>
                      <td>
                        <div className="hrms-person-cell">
                          <div className="avatar">{employee.fullName?.[0]?.toUpperCase() || "E"}</div>
                          <div>
                            <div className="hrms-row-title">{employee.fullName}</div>
                            <div className="hrms-row-subtitle">{employee.employeeCode} - {employee.department || "No department"}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <select value={draft.status || "Present"} onChange={(event) => updateDraft(employee.id, "status", event.target.value)}>
                          {STATUSES.map((item) => <option key={item}>{item}</option>)}
                        </select>
                      </td>
                      <td>
                        <input type="time" value={draft.checkInAt || ""} onChange={(event) => updateDraft(employee.id, "checkInAt", event.target.value)} />
                      </td>
                      <td>
                        <input type="time" value={draft.checkOutAt || ""} onChange={(event) => updateDraft(employee.id, "checkOutAt", event.target.value)} />
                      </td>
                      <td>
                        <input value={draft.note || ""} onChange={(event) => updateDraft(employee.id, "note", event.target.value)} placeholder="Optional" />
                      </td>
                      <td>
                        <button className="primary" onClick={() => saveRecord(employee.id)} disabled={savingId === employee.id}>
                          {savingId === employee.id ? "Saving..." : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
